import * as React from 'react';
import Media from 'react-media';

import { useLocation } from 'js/lib/useRouter';
import user from 'js/lib/user';

import { useCoachEpicEnabledState } from 'bundles/ai-course-coach/utils/productFeatureFlags';
import Help from 'bundles/common/components/Help';
import EnterpriseSearchBarV2, {
  UnifiedEnterpriseSearchBar,
} from 'bundles/enterprise-learner-search/components/search/EnterpriseSearchBarV2';
import MobileEnterpriseSearchBar, {
  UnifiedMobileEnterpriseSearchBar,
} from 'bundles/enterprise-learner-search/components/search/MobileEnterpriseSearchBar';
import PageFooter from 'bundles/page-footer/components/PageFooter';
import PageHeader, { PAGE_HEADER_MOBILE_BREAKPOINT } from 'bundles/page-header/components/PageHeader';
import PageNavLink from 'bundles/page-header/components/page-navigation/PageNavLink';
import PageNavigation from 'bundles/page-header/components/page-navigation/PageNavigation';
import useUserAgent from 'bundles/page/hooks/useUserAgent';
import EnterpriseModalManager from 'bundles/unified-common/components/EnterpriseModalManager';
import { useEnterpriseProductFeatures } from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import { useProgram } from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';
import useEnterprisePageHeaderLinks from 'bundles/unified-common/hooks/useEnterprisePageHeaderLinks';

/*
Use this to add the header/footer to a page in the enterprise context.

Ensure this is nested beneath the following providers: bundles/unified-common/providers/EnterpriseProductFeaturesDataProvider,
bundles/unified-common/providers/EnterpriseProgramDataProvider and bundles/unified-common/providers/ThirdPartyOrganizationDataProvider.
*/
const EnterpriseHeaderFooterWrapper = ({ children }: { children: React.ReactNode }) => {
  const program = useProgram();
  const userAgent = useUserAgent();
  const thirdPartyOrg = useThirdPartyOrganization();
  const features = useEnterpriseProductFeatures();
  const userId = user.get().id;
  const { pathname } = useLocation();

  /*
  We use useShouldShowCoachQuery to determine if we should render the help button. Note, this is not the global help button.
  useChatWidgetSelector doesn't handle this case because it doesn't differentiate between "loading" and "don't show any of the buttons", this is why useShouldShowCoachQuery is used instead.

  The last condition in renderHelpButton ensures it is falsy on CA pages, allowing ProgramHomeWrapper to render a single help icon / avoid overlapping buttons in the bottom right corner.
  */
  const { showEmbeddedCoach } = useCoachEpicEnabledState();
  const isCoachEnabled = showEmbeddedCoach;

  const renderHelpButton = !isCoachEnabled && !pathname.startsWith('/career-academy');

  const pageHeaderLinks = useEnterprisePageHeaderLinks();

  let desktopSearchBar: JSX.Element | null = null;
  let mobileSearchBar: JSX.Element | null = null;

  if (userId && program && thirdPartyOrg) {
    desktopSearchBar = (
      <UnifiedEnterpriseSearchBar
        programSlug={program.metadata?.slug}
        programName={program.metadata?.name}
        thirdPartyOrgSlug={thirdPartyOrg.slug}
        thirdPartyOrganizationId={thirdPartyOrg.id}
        userId={userId}
        programId={program.id}
        enableSkillsInSearchAndBrowse={features?.enableSkillsInSearchAndBrowse}
        shouldShowShortFormContent={features?.allowShortFormContentBasedOnAudience}
      />
    );
    mobileSearchBar = (
      <UnifiedMobileEnterpriseSearchBar
        programSlug={program.metadata?.slug}
        programName={program.metadata?.name}
        thirdPartyOrgSlug={thirdPartyOrg.slug}
        thirdPartyOrganizationId={thirdPartyOrg.id}
        userId={userId}
        programId={program.id}
        enableSkillsInSearchAndBrowse={features?.enableSkillsInSearchAndBrowse}
        shouldShowShortFormContent={features?.allowShortFormContentBasedOnAudience}
      />
    );
  } else if (program && thirdPartyOrg) {
    desktopSearchBar = (
      <EnterpriseSearchBarV2
        programIds={[program.id]}
        enableSkillsInSearchAndBrowse={!!features?.enableSkillsInSearchAndBrowse}
        programName={program.metadata?.name}
        shouldShowShortFormContent={features?.allowShortFormContentBasedOnAudience}
      />
    );
    mobileSearchBar = (
      <MobileEnterpriseSearchBar
        programIds={[program.id]}
        enableSkillsInSearchAndBrowse={!!features?.enableSkillsInSearchAndBrowse}
        programName={program.metadata?.name}
        shouldShowShortFormContent={features?.allowShortFormContentBasedOnAudience}
      />
    );
  }

  return (
    <React.Fragment>
      <Media query={{ maxWidth: `${PAGE_HEADER_MOBILE_BREAKPOINT}px` }} defaultMatches={userAgent?.isMobile}>
        {(matches) => (
          <PageHeader
            showLanguagesDropdown
            hideMetaNav
            thirdPartyOrganizationId={thirdPartyOrg?.id}
            hideEnterprise
            showGDPRBanner
            isEnterprise
            programId={program?.id}
            logoWrapper="div"
            injectedSearchBar={matches ? mobileSearchBar : desktopSearchBar}
            subNavigationLinks={
              !!pageHeaderLinks?.length && (
                <PageNavigation>
                  {pageHeaderLinks.map((link) => (
                    <PageNavLink
                      key={link.href}
                      label={link.label}
                      name={link.label.toLowerCase()}
                      href={link.href}
                      isCurrent={link.isCurrent}
                    />
                  ))}
                </PageNavigation>
              )
            }
            mobileSubNavigationLinks={
              pageHeaderLinks?.length
                ? pageHeaderLinks.map((link) => ({
                    href: link.href,
                    label: link.label,
                    name: link.label.toLowerCase(),
                  }))
                : undefined
            }
          />
        )}
      </Media>
      <main>{children}</main>
      <PageFooter />
      <EnterpriseModalManager />
      {renderHelpButton && <Help style={{ minWidth: 0, bottom: 12, right: 12 }} isEnterprise />}
    </React.Fragment>
  );
};

export default EnterpriseHeaderFooterWrapper;
